import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component,  Inject, OnDestroy, OnInit, Optional, PLATFORM_ID, Renderer2, input } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { debounceTime, Observable, filter, share, startWith, Subject, takeUntil, switchMap } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { AcvisService } from '@app/services/acvis.service';
import { HotelFiltersService } from '@app/services/hotelfilters.service';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { Airport } from '@app/models/hotels/Airport';
import { MEDIA_TOKEN } from '../../../../../tockens';

@Component({
    selector: 'clever-airport',
    templateUrl: './airport.component.html',
    styleUrls: ['./airport.component.scss'],
    animations: [
        trigger('zoom', [
            state('leftBottom', style({ transform: 'scale(0.3)', transformOrigin: 'left bottom', opacity: '0' })),
            state('default', style({ transform: 'scale(1)', opacity: '1' })),
            transition('* <=> default', animate('10ms cubic-bezier(0.25,1,.25,1)'))
        ])
    ],
    standalone: false
})
export class AirportComponent implements OnInit, OnDestroy {
    readonly mode = input<string>(undefined);
    readonly title = input<string>('Alle');
    readonly ziel = input<string>('Abflughafen');
    readonly inAll = input<boolean>(false);
    readonly bottomleftradius = input<boolean>(true);
    readonly norightborder = input<boolean>(true);
    protected unsubscribe$ = new Subject();
    isBrowser: boolean = false;
    showModal: boolean = false;
    focus: boolean = true;
    media: string = 'lg';
    origin = 'default';
    zielText = 'Alle';
    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList: Airport[] = [];


    tmp_nord: any[] = [];
    tmp_sud: any[] = [];
    tmp_ost: any[] = [];
    tmp_west: any[] = [];

    flughafen_nord: boolean = false;
    flughafen_sud: boolean = false;
    flughafen_ost: boolean = false;
    flughafen_west: boolean = false;

    // Airports static
    de_flughafen = [
        {
            name: 'Berlin-Brandenburg',
            code: 'BER',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Bremen',
            code: 'BRE',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Dortmund',
            code: 'DTM',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Dresden',
            code: 'DRS',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Düsseldorf',
            code: 'DUS',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Erfurt',
            code: 'ERF',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Frankfurt am Main',
            code: 'FRA',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Frankfurt-Hahn',
            code: 'HHN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Friedrichshafen',
            code: 'FDH',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Hamburg',
            code: 'HAM',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Hannover',
            code: 'HAJ',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Karlsruhe / Baden-Baden',
            code: 'FKB',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Kassel',
            code: 'KSF',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Köln-Bonn',
            code: 'CGN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Leipzig/Halle',
            code: 'LEJ',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Lübeck',
            code: 'LBC',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Magdeburg / Cochstedt',
            code: 'CSO',
            region: 'ost',
            countryCode: 'DE'
        },
        {
            name: 'Memmingen',
            code: 'FMM',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'München',
            code: 'MUC',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Münster-Osnabrück',
            code: 'FMO',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Nürnberg',
            code: 'NUE',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Paderborn',
            code: 'PAD',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Rostock',
            code: 'RLG',
            region: 'nord',
            countryCode: 'DE'
        },
        {
            name: 'Saarbrücken',
            code: 'SCN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Stuttgart',
            code: 'STR',
            region: 'sud',
            countryCode: 'DE'
        },
        {
            name: 'Weeze / Niederrhein',
            code: 'NRN',
            region: 'west',
            countryCode: 'DE'
        },
        {
            name: 'Westerland/Sylt',
            code: 'GWT',
            region: 'nord',
            countryCode: 'DE'
        },
    ];

    fr_flughafen = [
        {
            name: 'Strasbourg',
            code: 'SXB',
            countryCode: 'FR'
        },
    ];

    es_flughafen = [
        {
            name: 'Mallorca',
            code: 'PMI',
            countryCode: 'ES'
        },
    ];

    lu_flughafen = [
        {
            name: 'Luxemburg',
            code: 'LUX',
            countryCode: 'LU'
        },
    ];

    be_flughafen = [
        {
            name: 'Brüssel',
            code: 'BRU',
            countryCode: 'BE'
        },
        {
            name: 'Charleroi',
            code: 'CRL',
            countryCode: 'BE'
        },
        {
            name: 'Lüttich',
            code: 'LGG',
            countryCode: 'BE'
        },
    ];

    nl_flughafen = [
        {
            name: 'Amsterdam',
            code: 'AMS',
            countryCode: 'NL'
        },
        {
            name: 'Eindhoven',
            code: 'EIN',
            countryCode: 'NL'
        },
        {
            name: 'Groningen',
            code: 'GRQ',
            countryCode: 'NL'
        },
        {
            name: 'Maastricht',
            code: 'MST',
            countryCode: 'NL'
        },
    ];

    at_flughafen = [
        {
            name: 'Graz',
            code: 'GRZ',
            countryCode: 'AT'
        },
        {
            name: 'Innsbruck',
            code: 'INN',
            countryCode: 'AT'
        },
        {
            name: 'Klagenfurt',
            code: 'KLU',
            countryCode: 'AT'
        },
        {
            name: 'Linz',
            code: 'LNZ',
            countryCode: 'AT'
        },
        {
            name: 'Salzburg',
            code: 'SZG',
            countryCode: 'AT'
        },
        {
            name: 'Wien',
            code: 'VIE',
            countryCode: 'AT'
        }
    ];

    ch_flughafen = [
        {
            name: 'Basel-Mulhouse',
            code: 'BSL',
            countryCode: 'CH'
        },
        {
            name: 'Bern',
            code: 'BRN',
            countryCode: 'CH'
        },
        {
            name: 'Genf',
            code: 'GVA',
            countryCode: 'CH'
        },
        {
            name: 'St.Gallen/Altenrhein',
            code: 'ACH',
            countryCode: 'CH'
        },
        {
            name: 'Zürich',
            code: 'ZRH',
            countryCode: 'CH'
        }
    ];

    pl_flughafen = [
        {
            name: 'Krakau',
            code: 'KRK',
            countryCode: 'PL'
        },
        {
            name: 'Warschau',
            code: 'WAW',
            countryCode: 'PL'
        }
    ];

    dk_flughafen = [
        {
            name: 'Billund',
            code: 'BLL',
            countryCode: 'DK'
        }
    ];

    cz_flughafen = [
        {
            name: 'Prag',
            code: 'PRG',
            countryCode: 'CZ'
        }
    ];

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected cdr: ChangeDetectorRef,
        protected acvisService: AcvisService,
        protected render: Renderer2,
        protected hotelService: HotelFiltersService,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.media = this.media_token;
    }

    ngOnInit(): void {
        this.zielText = this.title();
        if (this.isBrowser) {
            this.initMedia();
            this.initSearchChange();
            this.initStaticAirports();
        }
    }

    async initStaticAirports() {
        for (let i = 0; i < this.de_flughafen.length; i++) {
            if (this.de_flughafen[i].region === 'sud') {
                this.tmp_sud.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'nord') {
                this.tmp_nord.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'ost') {
                this.tmp_ost.push(this.de_flughafen[i]);
            }
            if (this.de_flughafen[i].region === 'west') {
                this.tmp_west.push(this.de_flughafen[i]);
            }
        }
        this.initFilterSub();
    }

    initSearchChange() {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            // debounceTime(100),
            filter(value => (typeof value === 'string' && value?.length > 0)),
            share()
        );

        this.searchObj = filterShare.pipe(
            switchMap(value => this.acvisService.request({
                searchValue: value,
                inAll: this.inAll()
            }, 'airoportListCompletions'))
        );
    }

    initFilterSub() {
        this.hotelService.getfilters$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((filter: ParamFilters) => {
            this.initFilter(filter);
            this.checkRegion();
        });
    }

    async initFilter(filter) {
        if (this.inAll()) {
            if (filter?.arrivalAirportList?.length > 0) {
                if (JSON.stringify(this.selectedList) !== JSON.stringify(filter?.arrivalAirportList)) {
                    this.selectedList = JSON.parse(JSON.stringify(filter?.arrivalAirportList));
                    this.zielText = this.selectedList.length + '';
                }
            } else {
                this.zielText = this.title();
                this.selectedList = [];
            }
        } else {
            if (filter?.airportsResult?.length > 0) {
                if (JSON.stringify(this.selectedList) !== JSON.stringify(filter?.airportsResult)) {
                    this.selectedList = JSON.parse(JSON.stringify(filter?.airportsResult));
                    this.zielText = this.selectedList.length + '';
                }
            } else {
                this.zielText = this.title();
                this.selectedList = [];
            }
        }
    }

    async checkRegion() {
        let tmp_sud_ck = true;
        for (let i = 0; i < this.tmp_sud.length; i++) {
            if(this.isNotInlist(this.tmp_sud[i])) {
                tmp_sud_ck = false;
                i = this.tmp_sud.length;
            }
        }
        this.flughafen_sud = tmp_sud_ck;

        let tmp_nord_ck = true;
        for (let i = 0; i < this.tmp_nord.length; i++) {
            if(this.isNotInlist(this.tmp_nord[i])) {
                tmp_nord_ck = false;
                i = this.tmp_nord.length;
            }
        }
        this.flughafen_nord = tmp_nord_ck;

        let tmp_ost_ck = true;
        for (let i = 0; i < this.tmp_ost.length; i++) {
            if(this.isNotInlist(this.tmp_ost[i])) {
                tmp_ost_ck = false;
                i = this.tmp_ost.length;
            }
        }
        this.flughafen_ost = tmp_ost_ck;

        let tmp_west_ck = true;
        for (let i = 0; i < this.tmp_west.length; i++) {
            if(this.isNotInlist(this.tmp_west[i])) {
                tmp_west_ck = false;
                i = this.tmp_west.length;
            }
        }
        this.flughafen_west = tmp_west_ck;
        this.cdr.detectChanges();
    }

    async initMedia() {
       if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }

    
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    async openModal(ev) {
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    async openModalMobile(ev) {
        // this.render.addClass(document.body, 'model-open');
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    async closeModal() {
        this.showModal = false;
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    async closeModalMobile() {
        this.take();
    }

    async zoomin(position: any) {
        this.origin = position;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.origin = 'default';
            this.cdr.detectChanges();
        });
    }

    async clearText() {
        this.mySearchControl.setValue('');
        this.focus = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.focus = true;
            this.cdr.detectChanges();
        }, 50);
    }

    async selectValue(item) {
        this.selectedList.push({
            name: item.name,
            code: item.code,
        });
        this.zielText = this.selectedList.length + '';
        this.cdr.detectChanges();
    }

    async removeValue(index) {
        this.selectedList.splice(index, 1);
        this.zielText = this.selectedList.length + '';
        this.cdr.detectChanges();
    }

    async addAirport(item) {
        const index = this.indexInlist(item);
        if (index >= 0) {
            this.removeValue(index);
        } else {
            this.selectValue(item);
        }
        this.checkRegion();
    }

    indexInlist(item): number {
        for (let i = 0; i < this.selectedList.length; i++) {
            if (this.selectedList[i].code === item?.code) {
                return i;
            }
        }
        return -1;
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList.length; i++) {
            if (this.selectedList[i].code === item?.code) {
                return false;
            }
        }
        return true;
    }

    async removeAllSelected() {
        this.selectedList = [];
        this.zielText = this.title();
        this.flughafen_nord = false;
        this.flughafen_sud = false;
        this.flughafen_ost = false;
        this.flughafen_west = false;
        this.cdr.detectChanges();
    }

    async removeFromSelected(k) {
        if (this.selectedList[k]) {
            this.selectedList.splice(k, 1);
        }
        this.zielText = this.selectedList.length > 0 ? this.selectedList.length + '' : this.title();
        this.checkRegion();
    }

    async take() {
        if (this.inAll()) {
            this.hotelService.arrivalAirports(JSON.parse(JSON.stringify(this.selectedList)), this.mode() !== 'home');
        } else {
            this.hotelService.airportsResult(JSON.parse(JSON.stringify(this.selectedList)), this.mode() !== 'home');
        }
        this.closeModal();
    }

    checkNord(): void {
        this.flughafen_nord = !this.flughafen_nord;
        for (let i = 0; i < this.tmp_nord.length; i++) {
            const id = this.indexInlist(this.tmp_nord[i]);
            if (!this.flughafen_nord && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_nord && id < 0) {
                this.selectValue(this.tmp_nord[i]);
            }
        }
    }

    checkSud(): void {
        this.flughafen_sud = !this.flughafen_sud;
        for (let i = 0; i < this.tmp_sud.length; i++) {
            const id = this.indexInlist(this.tmp_sud[i]);
            if (!this.flughafen_sud && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_sud && id < 0) {
                this.selectValue(this.tmp_sud[i]);
            }
        }
    }

    checkOst(): void {
        this.flughafen_ost = !this.flughafen_ost;
        for (let i = 0; i < this.tmp_ost.length; i++) {
            const id = this.indexInlist(this.tmp_ost[i]);
            if (!this.flughafen_ost && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_ost && id < 0) {
                this.selectValue(this.tmp_ost[i]);
            }
        }
    }

    checkWest(): void {
        this.flughafen_west = !this.flughafen_west;
        for (let i = 0; i < this.tmp_west.length; i++) {
            const id = this.indexInlist(this.tmp_west[i]);
            if (!this.flughafen_west && id >= 0) {
                this.removeValue(id);
            } else if (this.flughafen_west && id < 0) {
                this.selectValue(this.tmp_west[i]);
            }
        }
    }
}
